import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";
import {
  ArrowLeft, RefreshCw, Users, BarChart3, Settings, List,
  Sparkles, Scissors, Palette, Heart, Utensils, Dumbbell,
  ChevronRight, Plus, Eye, UserPlus, Award, ClipboardList, FileText,
  Layers, Building2, ListChecks
} from "lucide-react";

// Tab Components
import OverviewTab from "./tabs/OverviewTab";
import AnalyticsTab from "./tabs/AnalyticsTab";
import PeopleTab from "./tabs/PeopleTab";
import ListsTab from "./tabs/ListsTab";
import EnhancedSettingsTab from "./tabs/EnhancedSettingsTab";
import BrandAdvocatesTab from "./tabs/BrandAdvocatesTab";
import TemplateEditorTab from "./tabs/TemplateEditorTab";
import TemplateContentEditor from "@/components/admin/TemplateContentEditor";
import PilotOverrideEditor from "@/components/admin/PilotOverrideEditor";
import ConsolidatedOverridesView from "@/components/admin/ConsolidatedOverridesView";

// Category icons mapping
const CATEGORY_ICONS = {
  hair: Scissors,
  makeup: Palette,
  "makeup-artists": Palette,
  mua: Palette,
  nail: Sparkles,
  nails: Sparkles,
  "nail-technicians": Sparkles,
  lash: Eye,
  "lash-artists": Eye,
  wellness: Heart,
  culinary: Utensils,
  "culinary-professionals": Utensils,
  instructor: Dumbbell,
  "instructors-educators": Dumbbell,
  event: Sparkles,
  events: Sparkles,
  "events-experiences": Sparkles,
  default: Sparkles
};

// Category colors
const CATEGORY_COLORS = {
  hair: { bg: "bg-purple-500", light: "bg-purple-50", text: "text-purple-600", border: "border-purple-200", hex: "#8b5cf6" },
  "hair-stylists": { bg: "bg-purple-500", light: "bg-purple-50", text: "text-purple-600", border: "border-purple-200", hex: "#8b5cf6" },
  makeup: { bg: "bg-pink-500", light: "bg-pink-50", text: "text-pink-600", border: "border-pink-200", hex: "#ec4899" },
  "makeup-artists": { bg: "bg-pink-500", light: "bg-pink-50", text: "text-pink-600", border: "border-pink-200", hex: "#ec4899" },
  mua: { bg: "bg-pink-500", light: "bg-pink-50", text: "text-pink-600", border: "border-pink-200", hex: "#ec4899" },
  nail: { bg: "bg-rose-500", light: "bg-rose-50", text: "text-rose-600", border: "border-rose-200", hex: "#f43f5e" },
  nails: { bg: "bg-rose-500", light: "bg-rose-50", text: "text-rose-600", border: "border-rose-200", hex: "#f43f5e" },
  "nail-technicians": { bg: "bg-rose-500", light: "bg-rose-50", text: "text-rose-600", border: "border-rose-200", hex: "#f43f5e" },
  lash: { bg: "bg-violet-500", light: "bg-violet-50", text: "text-violet-600", border: "border-violet-200", hex: "#a855f7" },
  "lash-artists": { bg: "bg-violet-500", light: "bg-violet-50", text: "text-violet-600", border: "border-violet-200", hex: "#a855f7" },
  wellness: { bg: "bg-emerald-500", light: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", hex: "#10b981" },
  culinary: { bg: "bg-orange-500", light: "bg-orange-50", text: "text-orange-600", border: "border-orange-200", hex: "#f97316" },
  "culinary-professionals": { bg: "bg-orange-500", light: "bg-orange-50", text: "text-orange-600", border: "border-orange-200", hex: "#f97316" },
  instructor: { bg: "bg-sky-500", light: "bg-sky-50", text: "text-sky-600", border: "border-sky-200", hex: "#0ea5e9" },
  "instructors-educators": { bg: "bg-sky-500", light: "bg-sky-50", text: "text-sky-600", border: "border-sky-200", hex: "#0ea5e9" },
  event: { bg: "bg-blue-500", light: "bg-blue-50", text: "text-blue-600", border: "border-blue-200", hex: "#3b82f6" },
  events: { bg: "bg-blue-500", light: "bg-blue-50", text: "text-blue-600", border: "border-blue-200", hex: "#3b82f6" },
  "events-experiences": { bg: "bg-blue-500", light: "bg-blue-50", text: "text-blue-600", border: "border-blue-200", hex: "#3b82f6" },
  default: { bg: "bg-gray-500", light: "bg-gray-50", text: "text-gray-600", border: "border-gray-200", hex: "#6b7280" }
};

// Tab configuration - Renamed for clarity
// "Templates" -> "Pilot Onboarding" (intake for pilots joining program)
// "Content Editor" -> "Host Dashboard" (template for what hosts see)
// Removed "Pilot Instances" and "All Overrides" (merged into respective tabs / moved to Defaults & Inspo)
// Lists and Advocates are now sub-tabs under People (synced with Support Advocates Network in admin)
const TABS = [
  { id: "overview", label: "Overview", icon: Eye },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "people", label: "People", icon: Users },  // Now contains Lists + Advocates as sub-tabs
  { id: "templates", label: "Pilot Onboarding", icon: ClipboardList },
  { id: "content", label: "Host Dashboard", icon: FileText },
  { id: "settings", label: "Settings", icon: Settings }
];

// Sub-tabs for People section (synced with Support Advocates Network)
const PEOPLE_SUB_TABS = [
  { id: "members", label: "Members", icon: Users },
  { id: "lists", label: "Lists", icon: List },
  { id: "advocates", label: "Advocates", icon: Award }
];

export default function PilotsDashboard() {
  const { category, tab } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State - ALL hooks MUST be called before any conditional returns
  const [loading, setLoading] = useState(true);
  const [subCategoryId, setSubCategoryId] = useState(searchParams.get("id") || null);
  const [subCategoryData, setSubCategoryData] = useState(null);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(tab || "overview");
  const [activePeopleSubTab, setActivePeopleSubTab] = useState("members"); // Sub-tab for People section
  
  // Pilot instance state (for Pilot Instances tab)
  const [pilotProfiles, setPilotProfiles] = useState([]);
  const [selectedPilotProfileId, setSelectedPilotProfileId] = useState(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  
  // Check for invalid category
  const isInvalidCategory = !category || category === "undefined" || category === "null";
  
  // Derive the preset/category type for colors - use preset_id from portal data if available
  const effectiveCategory = subCategoryData?.preset_id || (() => {
    // Try to extract base preset from category name (e.g., "Makeup Artists" -> "makeup")
    const catLower = (category || "").toLowerCase();
    const presetKeywords = ["makeup", "hair", "nail", "lash", "wellness", "culinary", "instructor", "event", "mua"];
    for (const kw of presetKeywords) {
      if (catLower.includes(kw)) return kw;
    }
    return catLower;
  })();
  
  // Get category icon and colors using the effective category
  const CategoryIcon = CATEGORY_ICONS[effectiveCategory?.toLowerCase()] || CATEGORY_ICONS[category?.toLowerCase()] || CATEGORY_ICONS.default;
  const categoryColors = CATEGORY_COLORS[effectiveCategory?.toLowerCase()] || CATEGORY_COLORS[category?.toLowerCase()] || CATEGORY_COLORS.default;
  
  // Normalize category name for display
  const displayCategory = subCategoryData?.name || (category ? category.charAt(0).toUpperCase() + category.slice(1).toLowerCase() : "Pilot");

  // Fetch all sub-categories for this pilot type
  const fetchSubCategories = useCallback(async () => {
    if (isInvalidCategory) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Find the pilots category
        const pilotsCategory = data.categories?.find(c => c.type === "pilots");
        if (pilotsCategory) {
          // Filter sub-categories by the current category type
          const filtered = pilotsCategory.sub_categories.filter(
            sub => sub.name?.toLowerCase() === category?.toLowerCase() ||
                   sub.name?.toLowerCase().includes(category?.toLowerCase())
          );
          setAllSubCategories(filtered);
          
          // If we have a subCategoryId, use it; otherwise use the first match
          if (!subCategoryId && filtered.length > 0) {
            setSubCategoryId(filtered[0].sub_category_id);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch sub-categories:", err);
      toast.error("Failed to load pilot data");
    }
  }, [category, subCategoryId]);

  // Fetch specific sub-category details
  const fetchSubCategoryDetails = useCallback(async (subCatId) => {
    if (!subCatId) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories/pilots/sub-categories/${subCatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubCategoryData(data);
      }
    } catch (err) {
      console.error("Failed to fetch sub-category details:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch pilot profiles (instructor pilot instances)
  const fetchPilotProfiles = useCallback(async () => {
    setLoadingProfiles(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/instructor-pilots/profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPilotProfiles(data.profiles || []);
        // Auto-select first profile if none selected
        if (data.profiles?.length > 0 && !selectedPilotProfileId) {
          setSelectedPilotProfileId(data.profiles[0].profile_id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch pilot profiles:", err);
    } finally {
      setLoadingProfiles(false);
    }
  }, [selectedPilotProfileId]);

  // Initial fetch
  useEffect(() => {
    if (!isInvalidCategory) {
      fetchSubCategories();
    }
  }, [fetchSubCategories, isInvalidCategory]);

  // Fetch details when subCategoryId changes
  useEffect(() => {
    if (isInvalidCategory) return;
    if (subCategoryId) {
      fetchSubCategoryDetails(subCategoryId);
    } else {
      setLoading(false);
    }
  }, [subCategoryId, fetchSubCategoryDetails, isInvalidCategory]);

  // Update URL when tab changes
  useEffect(() => {
    if (isInvalidCategory) return;
    if (activeTab !== tab) {
      const newPath = `/admin/portals/pilots/${category}/${activeTab}`;
      const params = subCategoryId ? `?id=${subCategoryId}` : "";
      navigate(`${newPath}${params}`, { replace: true });
    }
  }, [activeTab, tab, category, subCategoryId, navigate, isInvalidCategory]);

  // Fetch pilot profiles when "instances" tab is active
  useEffect(() => {
    if (activeTab === "instances" && pilotProfiles.length === 0) {
      fetchPilotProfiles();
    }
  }, [activeTab, pilotProfiles.length, fetchPilotProfiles]);

  // Handle tab change
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };

  // Refresh data
  const handleRefresh = async () => {
    setLoading(true);
    await fetchSubCategories();
    if (subCategoryId) {
      await fetchSubCategoryDetails(subCategoryId);
    }
    setLoading(false);
    toast.success("Data refreshed");
  };

  // Navigate back to portals list
  const handleBack = () => {
    navigate("/admin/portals");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          <p className="text-gray-500">Loading {displayCategory} dashboard...</p>
        </div>
      </div>
    );
  }

  // Redirect if invalid category - placed AFTER all hooks but before UI renders
  if (isInvalidCategory) {
    return <Navigate to="/admin/portals" replace />;
  }

  // No sub-category found for this type
  if (!subCategoryData && !loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
          
          <Card className="max-w-md mx-auto">
            <CardContent className="pt-8 pb-8 text-center">
              <div className={`w-16 h-16 rounded-2xl ${categoryColors.bg} text-white flex items-center justify-center mx-auto mb-4`}>
                <CategoryIcon className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-semibold mb-2">No {displayCategory} Pilot Found</h2>
              <p className="text-gray-500 mb-6">Create a {displayCategory} pilot to get started with managing participants.</p>
              <Button onClick={handleBack}>
                <Plus className="w-4 h-4 mr-2" />
                Create {displayCategory} Pilot
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const members = subCategoryData?.members || [];
  const pendingInvites = subCategoryData?.pending_invites || [];
  const totalParticipants = members.length + pendingInvites.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageMeta title={`${displayCategory} Pilots`} description={`Manage ${displayCategory} pilot participants and settings`} />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack} className="shrink-0">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl ${categoryColors.bg} text-white flex items-center justify-center`}>
                <CategoryIcon className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{displayCategory} Pilots</h1>
                <p className="text-sm text-gray-500">
                  {totalParticipants} participant{totalParticipants !== 1 ? "s" : ""} • {members.length} active
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/pilots/categories")} data-testid="manage-categories-btn">
              <Settings className="w-4 h-4 mr-2" />
              Categories
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button size="sm" className={categoryColors.bg} onClick={() => { handleTabChange("people"); setTimeout(() => document.querySelector('[data-testid="add-participant-btn"]')?.click(), 300); }}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add Participant
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className={`${categoryColors.light} ${categoryColors.border} border`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{totalParticipants}</p>
                  <p className="text-sm text-gray-600">Total</p>
                </div>
                <Users className={`w-8 h-8 ${categoryColors.text} opacity-50`} />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600">{members.length}</p>
                  <p className="text-sm text-gray-600">Active</p>
                </div>
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  {members.length > 0 ? Math.round((members.length / totalParticipants) * 100) : 0}%
                </Badge>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-amber-600">{pendingInvites.length}</p>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
                <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                  Invited
                </Badge>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-600">
                    {members.filter(m => m.nda_signed).length}
                  </p>
                  <p className="text-sm text-gray-600">NDA Signed</p>
                </div>
                <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                  Verified
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Card>
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 h-auto">
              {TABS.map((tabItem) => {
                const TabIcon = tabItem.icon;
                return (
                  <TabsTrigger
                    key={tabItem.id}
                    value={tabItem.id}
                    className={`
                      rounded-none border-b-2 border-transparent 
                      data-[state=active]:border-current data-[state=active]:bg-transparent 
                      px-4 py-3 text-sm font-medium
                      data-[state=active]:${categoryColors.text}
                    `}
                    data-testid={`tab-${tabItem.id}`}
                  >
                    <TabIcon className="w-4 h-4 mr-2" />
                    {tabItem.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <TabsContent value="overview" className="p-0 m-0">
              <OverviewTab 
                subCategoryData={subCategoryData}
                categoryColors={categoryColors}
                category={effectiveCategory || category}
                onRefresh={handleRefresh}
              />
            </TabsContent>

            <TabsContent value="analytics" className="p-0 m-0">
              <AnalyticsTab 
                subCategoryData={subCategoryData}
                categoryColors={categoryColors}
                category={effectiveCategory || category}
              />
            </TabsContent>

            <TabsContent value="people" className="p-0 m-0">
              {/* People Section with Sub-tabs: Members, Lists, Advocates */}
              <div className="p-4 border-b bg-gray-50">
                <div className="flex items-center gap-2">
                  {PEOPLE_SUB_TABS.map((subtab) => {
                    const SubIcon = subtab.icon;
                    return (
                      <Button
                        key={subtab.id}
                        variant={activePeopleSubTab === subtab.id ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setActivePeopleSubTab(subtab.id)}
                        className="gap-2"
                        data-testid={`people-subtab-${subtab.id}`}
                      >
                        <SubIcon className="w-4 h-4" />
                        {subtab.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
              
              {/* Sub-tab Content */}
              {activePeopleSubTab === "members" && (
                <PeopleTab 
                  subCategoryData={subCategoryData}
                  categoryColors={categoryColors}
                  category={effectiveCategory || category}
                  subCategoryId={subCategoryId}
                  onRefresh={handleRefresh}
                />
              )}
              
              {activePeopleSubTab === "lists" && (
                <ListsTab 
                  subCategoryData={subCategoryData}
                  categoryColors={categoryColors}
                  category={effectiveCategory || category}
                />
              )}
              
              {activePeopleSubTab === "advocates" && (
                <BrandAdvocatesTab 
                  subCategoryData={subCategoryData}
                  categoryColors={categoryColors}
                  category={effectiveCategory || category}
                  subCategoryId={subCategoryId}
                  onRefresh={handleRefresh}
                />
              )}
            </TabsContent>

            <TabsContent value="instances" className="p-0 m-0">
              <div className="p-6">
                {loadingProfiles ? (
                  <div className="flex items-center justify-center p-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : pilotProfiles.length === 0 ? (
                  <Card>
                    <CardContent className="pt-8 pb-8 text-center">
                      <Layers className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-semibold mb-2">No Pilot Instances</h3>
                      <p className="text-gray-500 mb-4">
                        Create a pilot instance to customize content and manage hosts for specific pilots.
                      </p>
                      <Button onClick={() => toast.info("Coming soon: Create new pilot instance")}>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Pilot Instance
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {/* Pilot Profile Selector */}
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Select Pilot Instance
                        </CardTitle>
                        <CardDescription>
                          Choose a pilot to view and edit its specific overrides
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-3">
                          <Select
                            value={selectedPilotProfileId || ""}
                            onValueChange={(value) => setSelectedPilotProfileId(value)}
                          >
                            <SelectTrigger className="flex-1" data-testid="pilot-profile-selector">
                              <SelectValue placeholder="Select a pilot instance..." />
                            </SelectTrigger>
                            <SelectContent>
                              {pilotProfiles.map((profile) => (
                                <SelectItem 
                                  key={profile.profile_id} 
                                  value={profile.profile_id}
                                  data-testid={`pilot-option-${profile.profile_id}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span>{profile.name}</span>
                                    {profile.org?.name && (
                                      <span className="text-xs text-gray-400">
                                        ({profile.org.name})
                                      </span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={fetchPilotProfiles}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pilot Override Editor */}
                    {selectedPilotProfileId && (
                      <PilotOverrideEditor
                        profileId={selectedPilotProfileId}
                        pilotName={pilotProfiles.find(p => p.profile_id === selectedPilotProfileId)?.name || "Pilot"}
                        templateId={pilotProfiles.find(p => p.profile_id === selectedPilotProfileId)?.template_id || "instructor_pilot"}
                      />
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="overrides" className="p-0 m-0">
              <div className="p-6">
                <ConsolidatedOverridesView />
              </div>
            </TabsContent>

            <TabsContent value="templates" className="p-0 m-0">
              <TemplateEditorTab 
                subCategoryData={subCategoryData}
                categoryColors={categoryColors}
                category={effectiveCategory || category}
                subCategoryId={subCategoryId}
                onRefresh={handleRefresh}
              />
            </TabsContent>

            <TabsContent value="content" className="p-0 m-0">
              <div className="p-6">
                <TemplateContentEditor 
                  dashboardType={effectiveCategory?.toLowerCase().includes("instructor") ? "instructor_pilot" : "event_pilot"}
                  onSave={handleRefresh}
                />
              </div>
            </TabsContent>

            <TabsContent value="settings" className="p-0 m-0">
              <EnhancedSettingsTab 
                subCategoryData={subCategoryData}
                categoryColors={categoryColors}
                category={effectiveCategory || category}
                subCategoryId={subCategoryId}
                onRefresh={handleRefresh}
              />
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}
